import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from './tokens'
import Reveal from './Reveal'
import Link from '../utility/CustomLink'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '56px 0',
    background: T.color.canvas,
    [theme.breakpoints.down('sm')]: {
      padding: '36px 0',
    },
  },
  // Outer box only constrains width (max-width + centering). The 16:9 aspect
  // box below must be a SEPARATE element: CSS resolves padding-top percentages
  // against the *containing block's* width, not this element's own resolved
  // max-width. Combining both on one node made padding-top:56.25% compute
  // against the (wider) parent Container instead of this box's 900px cap,
  // squashing the video (observed ratio 0.72 instead of 0.5625).
  wrapper: {
    maxWidth: 900,
    margin: '0 auto',
  },
  aspectBox: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    borderRadius: T.radius.lg,
    boxShadow: T.shadow.lg,
    overflow: 'hidden',
    background: T.color.surface,
  },
  iframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    border: 0,
  },
  cover: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    border: 0,
    padding: 0,
    margin: 0,
    background: T.color.surface,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:focus-visible': {
      outline: `3px solid ${T.color.brand}`,
      outlineOffset: -3,
    },
    '&:hover $playBtn': {
      background: 'rgba(26,26,46,0.30)',
      transform: 'scale(1.06)',
      '@media (prefers-reduced-motion: reduce)': {
        transform: 'none',
      },
    },
  },
  coverTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    fontFamily: T.font.heading,
    fontSize: 24,
    fontWeight: 600,
    color: T.color.ink,
    margin: 0,
    padding: '0 24px',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      gap: '6px',
    },
  },
  coverLogo: {
    height: 26,
    width: 'auto',
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      height: 18,
    },
  },
  playBtn: {
    marginTop: 28,
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'rgba(26,26,46,0.22)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.25s ease, background 0.25s ease',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: 56,
      height: 56,
      marginTop: 20,
    },
  },
  playTriangle: {
    width: 0,
    height: 0,
    marginLeft: 6,
    borderTop: '13px solid transparent',
    borderBottom: '13px solid transparent',
    borderLeft: '22px solid rgba(26,26,46,0.55)',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 4,
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      borderLeft: '17px solid rgba(26,26,46,0.55)',
    },
  },
  moreLink: {
    position: 'absolute',
    // Padding enlarges the tap target to ≥48px tall (Lighthouse tap-targets)
    // while the counter-reduced right/bottom keep the text visually in place.
    right: 6,
    bottom: 2,
    padding: '14px 14px',
    fontFamily: T.font.body,
    fontSize: 14,
    fontWeight: 600,
    color: T.color.brand,
    textDecoration: 'none',
    zIndex: 1,
    '&:hover': {
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
      right: 0,
      bottom: 0,
    },
  },
}))

const BusinessVideoNew = () => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  const [isPlaying, setIsPlaying] = useState(false)
  const VIDEO_TITLE = t('businessVideo.title')

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <div className={classes.wrapper}>
            <div className={classes.aspectBox}>
              {isPlaying ? (
                <iframe
                  className={classes.iframe}
                  src="https://www.youtube-nocookie.com/embed/nTuePVYLifg?autoplay=1&rel=0"
                  title={VIDEO_TITLE}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <>
                  <button
                    type="button"
                    className={classes.cover}
                    aria-label={t('businessVideo.playAria', { title: VIDEO_TITLE })}
                    onClick={() => setIsPlaying(true)}
                  >
                    <h3 className={classes.coverTitle}>
                      <span>{t('businessVideo.coverBefore')}</span>
                      <img
                        src="/static/img/sp-logo.svg"
                        alt="SHIPPOP"
                        className={classes.coverLogo}
                        width="144"
                        height="26"
                        loading="lazy"
                        decoding="async"
                      />
                      <span>Pro</span>
                    </h3>
                    <span className={classes.playBtn} aria-hidden="true">
                      <span className={classes.playTriangle} />
                    </span>
                  </button>
                  <Link href="/pro" passHref>
                    <a className={classes.moreLink}>{t('businessVideo.more')}</a>
                  </Link>
                </>
              )}
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default BusinessVideoNew
